import { useCallback, useState, useRef } from 'react'

import { useMutation } from '@tanstack/react-query'

import { SearchFusionKind } from '@tribeplatform/gql-client/types'

import { useTribeClient } from '../../useTribeClient.js'
import { ASK_AI_SUBSCRIPTION } from './AskAI.constants.js'
import { AskAIInput, AskAIIterator, AskSubscription } from './AskAI.types.js'

export const useAskAI = () => {
  const { client } = useTribeClient()
  const [response, setResponse] = useState('')
  const iteratorRef = useRef<AskAIIterator | null>(null)

  const processStream = async (iterator: AskAIIterator) => {
    try {
      // eslint-disable-next-line no-restricted-syntax
      for await (const data of iterator) {
        setResponse(data.ask)
      }
    } catch (error) {
      if (error instanceof CloseEvent) {
        throw new Error('Connection closed!', { cause: error })
      } else if (error instanceof Error) {
        throw error
      } else {
        throw new Error('Unknown error occurred')
      }
    }
  }

  const { mutateAsync, isLoading, isError, error } = useMutation<
    void,
    Error,
    AskAIInput
  >({
    mutationFn: async ({ question }) => {
      setResponse('')
      const iterator = client.websocket.iterate<AskSubscription>({
        query: ASK_AI_SUBSCRIPTION.loc.source.body,
        variables: {
          q: question,
          searchConfig: {
            fusionKind: SearchFusionKind.rrf,
            fusionScoreThreshold: 0.2,
            fullTextEnabled: true,
            vectorTextEnabled: true,
            vectorEmbedEnabled: true,
            vectorTextScoreThreshold: 0.45,
            vectorEmbedScoreThreshold: 0.45,
          },
        },
      })
      iteratorRef.current = iterator
      await processStream(iterator)
    },
  })

  const askAI = useCallback(
    async (input: AskAIInput) => {
      // cancel previous iterator if it exists
      if (iteratorRef.current) {
        await iteratorRef.current?.return()
      }

      setResponse('')

      return mutateAsync(input)
    },
    [mutateAsync],
  )

  return {
    askAI,
    response,
    isLoading,
    isError,
    error,
  }
}
